import { setFocus } from "@noriginmedia/norigin-spatial-navigation"
import Hls, { HlsConfig, LoaderResponse } from "hls.js"
import mux from "mux-embed"
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { PlayerEvent, playerStore, usePlayList } from "src/store/PlayerStore"
import { captureSentryException, setSentryTag } from "src/utilities/sentry"

import { useHelper } from "../../hooks/use-helper"
import useSdk, { ErrorLevel, StreamManifestType } from "../../hooks/use-sdk"
import useTranslate from "../../hooks/use-translate"
import { appStore } from "../../store/AppStore"
import { getPlayerCapabilities } from "../../utilities/common"
import {
  canPlayFlacAudio,
  env,
  Platform,
  platform,
} from "../../utilities/constants"
import {
  adjustToStreamingSettingsHlsJs,
  removeIncompatibleLevels,
} from "../../utilities/streaming-helpers"
import { TI } from "../../utilities/theme"
import { DGText } from "../common/DGText"

import { PlayerUIFocusKeys } from "./player-ui-focus-keys"
import { useProgress } from "./use-progress"

const getHostName = (url?: string) => {
  if (!url) return ""
  try {
    return new URL(url).hostname
  } catch (e) {
    captureSentryException(e)
    return ""
  }
}

const muxKey = env.muxKey

const DEBUG_OVERLAY = false

const replaceAll = (str: string, search: string, replace: string) => {
  search = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return str.replace(new RegExp(search, "g"), replace)
}

function processManifest(
  playlist: LoaderResponse["data"],
  canPlayDolbyAudio: boolean,
) {
  if (typeof playlist === "string") {
    // DGAPP-5222:
    // remove all dolby codecs to ensure player doesn't pick it
    if (canPlayFlacAudio && !canPlayDolbyAudio)
      playlist = replaceAll(replaceAll(playlist, "ec-3,", ""), ",ec-3", "")
    // remove fLaC codecs on SmartTV (most smartTVs report that they can play flac, but they can't. So we remove it
    // to be sure). Also, the WebOS simulator seems to correctly report that it can't play flac.
    else if (!canPlayFlacAudio)
      playlist = replaceAll(
        replaceAll(replaceAll(playlist, "fLaC,", ""), ",fLaC", ""),
        ",fLaC,",
        "",
      )
    return playlist
  }
  return playlist
}

export const WebPlayer = () => {
  // Chromecast incorrectly identifies lossless as unsupported
  // -> Patch Hls compatibility check
  useEffect(() => {
    setEventListener(PlayerEvent.Play, () => (playbackRetries.current = 0), id)
    const originalIsTypeSupported = Hls.getMediaSource()!.isTypeSupported.bind(
      {},
    )
    Hls.getMediaSource()!.isTypeSupported = (type: string) => {
      if (platform !== Platform.Chromecast) {
        return originalIsTypeSupported(type)
      }
      return true
    }
    return () => {
      Hls.getMediaSource()!.isTypeSupported = originalIsTypeSupported
    }
  }, [])

  const hlsInstance = useRef<Hls>(null)

  const player = useRef<HTMLVideoElement>(null)
  const setVideoRef = playerStore((state) => state.setVideoRef)
  const setSubtitles = playerStore((state) => state.setSubtitles)
  const url = playerStore((state) => state.url)
  const setEventListener = playerStore((state) => state.setEventListener)
  const isPreview = playerStore((state) => state.isPreview)
  const streamingManifestType = playerStore(
    (state) => state.streamingManifestType,
  )
  const setHls = playerStore((state) => state.setHls)
  const setAudioTrack = playerStore((state) => state.setAudioTrack)
  const { open, startFromProgress, startAudioTrack } = playerStore(
    (state) => state.navigationProps,
  )
  const canPlayDolbyAudio = useMemo(() => window.capabilities.dolbyAtmos, [])
  // only set it once whenever requested by the play button, then switch off immediately after play

  const startFromProgressHelper = useRef<number | boolean | undefined>(false)
  useEffect(() => {
    startFromProgressHelper.current = startFromProgress
  }, [startFromProgress])
  const content = playerStore((state) => state.content)

  const playList = usePlayList(content)
  const trackId = useRef<string | undefined>(null)
  const trackIdState = playerStore((state) => state.trackId)
  const setTrackId = playerStore((state) => state.setTrackId)
  const currentSubTrack = playerStore((state) => state.currentSubTrack)
  const setPrompt = appStore((state) => state.setPrompt)
  const returnFocus = playerStore((state) => state.returnFocus)
  const resetPlayback = playerStore((state) => state.resetPlayback)
  const currentUser = appStore((state) => state.currentUser)
  const t = useTranslate()
  const sdk = useSdk(ErrorLevel.NoError)
  const id = useId()
  const playbackRetries = useRef(0)

  const tracking = appStore((state) => state.tracking)

  const streaming = appStore((state) => state.streaming)

  const shuffle = playerStore((state) => state.shuffle)
  const shuffledPlaylist = playerStore((state) => state.shuffledPlaylist)
  const repeat = playerStore((state) => state.repeat)

  useEffect(() => {
    trackId.current = trackIdState
  }, [playList, trackIdState])

  const derivedPlaylist = useMemo(
    () =>
      shuffle &&
      playList &&
      (content?.__typename === "EditorialPlaylist" ||
        content?.__typename === "Album")
        ? shuffledPlaylist
        : playList?.flatMap((set) => set.items),
    [playList, shuffle, shuffledPlaylist, content],
  )
  const playlistHelper = useHelper(derivedPlaylist)
  const derivedRepeat = useMemo(
    () =>
      repeat &&
      (content?.__typename === "EditorialPlaylist" ||
      content?.__typename === "Album"
        ? repeat
        : "none"),

    [repeat, content],
  )
  const repeatHelper = useHelper(derivedRepeat)

  useEffect(() => {
    if (!open) {
      try {
        player.current?.pause()
      } catch (e) {
        console.log(e)
        captureSentryException(e)
      }
      hlsInstance?.current?.destroy()
    }
  }, [open])

  // initialize player
  useEffect(() => {
    if (player.current && open) {
      const video = player.current
      setVideoRef(video)
    }
  }, [open, tracking])

  const { seek } = useProgress()

  const setNavigationProps = playerStore((state) => state.setNavigationProps)
  const navigationProps = playerStore((state) => state.navigationProps)
  const fetchNextUp = useCallback(async () => {
    const closePlayer = () => {
      setNavigationProps({ open: false })
      if (returnFocus) setFocus(returnFocus)
    }
    const allTracks = playlistHelper.current
    if (!allTracks) {
      closePlayer()
      return
    }
    const index = allTracks.findIndex((track) => track.id === trackId.current)
    if (index === -1 || !trackId.current) {
      closePlayer()
      return
    }
    if (repeatHelper.current === "single") {
      seek({ step: 0, relative: false, instant: true })
      player.current?.play()
      return
    }
    if (allTracks[index + 1]) {
      const track = allTracks[index + 1]
      setTrackId(track.id)
      return
    }
    if (repeatHelper.current === "all") {
      const track = allTracks[0]
      setTrackId(track.id)
      return
    }
    closePlayer()
    return
  }, [setTrackId, returnFocus])

  // this function needs to be called on duration update, because some TVs
  // can't seek immediately after pressing play
  const startPos = useCallback(async () => {
    if (content?.__typename === "LiveConcert") return undefined
    let mark: number | undefined = 0
    if (typeof startFromProgressHelper.current === "number")
      return startFromProgressHelper.current
    else if (
      !isPreview &&
      ((content?.__typename === "VodConcert" && trackIdState) ||
        content?.__typename === "Video") &&
      startFromProgressHelper.current
    ) {
      try {
        const progress = await sdk.playbackProgress({
          id: trackIdState || content?.id,
        })
        mark = progress?.playbackProgressByContentId?.mark
      } catch (e) {
        captureSentryException(e)
        mark = 0
      }
    }
    const duration =
      content?.__typename === "VodConcert"
        ? content.performanceWorks?.[0]?.duration
        : content?.__typename === "Video"
          ? content.duration
          : 0
    return mark && duration - mark > 10 ? mark : 0
  }, [trackIdState, isPreview, content])

  const onVideoLoaded = useCallback(async () => {
    setEventListener(
      PlayerEvent.Ended,
      () => {
        if (
          !isPreview &&
          playlistHelper.current &&
          content?.__typename !== "LiveConcert"
        )
          fetchNextUp()
        else {
          if (returnFocus) setFocus(returnFocus)
          setNavigationProps({ open: false })
        }
      },
      id,
    )
    player.current?.play().catch((e) => {
      console.log(e)
      captureSentryException(e)
    })
  }, [
    setEventListener,
    isPreview,
    fetchNextUp,
    id,
    trackIdState,
    startFromProgress,
  ])

  const loadVideo = async () => {
    const jwt = window.localStorage.getItem("Jwt")
    const mark = await startPos()
    if (!Hls.isSupported()) return
    const config: Partial<HlsConfig> = {
      startPosition: mark,
      nudgeOffset: 0.1,
      maxFragLookUpTolerance: 0.25,
      highBufferWatchdogPeriod: 5,
      nudgeMaxRetry: 10,
      maxBufferLength: 60,
      maxMaxBufferLength: 60,
      capLevelToPlayerSize: false,
      enableWebVTT: true,
      enableCEA708Captions: true,
      liveDurationInfinity: false,
      fragLoadingMaxRetryTimeout: 64000,
      forceKeyFrameOnDiscontinuity: false,
      progressive: false,
      liveSyncDurationCount: 3,
      initialLiveManifestSize: 1,
      abrBandWidthFactor: 0.98,
      abrBandWidthUpFactor: 0.6,
      enableSoftwareAES: false,
      fragLoadingMaxRetry: 8,
      levelLoadingMaxRetry: 6,
      backBufferLength: 0,
      maxBufferSize: 30000000,
      enableWorker: false,
      subtitlePreference: { forced: true },
    }
    /** Only apply this measure in Combined Manifests: Only combined manifests
     * are guaranteed to have AAC codecs alongside Atmos/fLaC, so it can
     * be safely adjusted like this
     */
    if (streamingManifestType === StreamManifestType.HlsCombined)
      config.loader = class extends Hls.DefaultConfig.loader {
        constructor(config: HlsConfig) {
          super(config)
          const load = this.load.bind(this)
          this.load = function (context, config, callbacks) {
            if ("type" in context && context.type === "manifest") {
              const onSuccess = callbacks.onSuccess
              callbacks.onSuccess = function (
                response,
                stats,
                context,
                networkDetails,
              ) {
                // rewrite the manifest if needed
                response.data = processManifest(
                  response.data,
                  canPlayDolbyAudio,
                )
                onSuccess(response, stats, context, networkDetails)
              }
            }
            load(context, config, callbacks)
          }
        }
      }
    hlsInstance.current = new Hls(config)
    const hls = hlsInstance.current
    const is4K = (await getPlayerCapabilities()).is4K

    // add hls to window for debugging
    window.hls = hls

    setHls(hls)
    if (hls && player.current) {
      // playback started successfully
      player.current!.onplaying = () => {
        setNavigationProps({ ...navigationProps, startFromProgress: false })
      }
      const video = player.current
      hls.subtitleDisplay = false
      hls.config.xhrSetup = function (xhr: XMLHttpRequest) {
        xhr.setRequestHeader("jwt", jwt ?? "")
      }
      hls.attachMedia(video)
      if (url) hls.loadSource(`${url}`)
      setSentryTag("playback_last", content?.id)
      setSentryTag("playing", content?.id)
      // mux tracking
      if (tracking.mux && muxKey) {
        mux.monitor(video, {
          debug: env.env === "staging",
          disableCookies: true,
          hlsjs: hlsInstance.current,
          Hls: Hls,
          data: {
            viewer_user_id: currentUser?.id?.toLowerCase(),
            env_key: muxKey,
            player_name: "SmartTV Player",
            player_init_time: Date.now(),
            // Video Metadata
            video_content_type: content?.__typename,
            video_id: trackIdState,
            video_title: content?.title,
            video_stream_type:
              content?.__typename === "LiveConcert" ? "live" : "on-demand",
            // get the CDN host from the video URL
            video_cdn: getHostName(url),
          },
        })
      }
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        onVideoLoaded()
        removeIncompatibleLevels(hls, is4K)
      })
      hls.on(Hls.Events.MANIFEST_LOADED, () => {
        setTimeout(() => {
          /**
           * hack alert: by empirical evidence, you can only set the audio track using this timeout function.
           * Doing it without a timeout resets the track entirely. Also, it has to be done twice. The reason being
           * that if you come from a different audio group, setting it once will switch to index = 0 in the other
           * audio group, and setting it again picks the correct track.
           */
          ;[...Array(2)].map(() =>
            adjustToStreamingSettingsHlsJs({
              hls,
              preferDolbyAtmos: streaming.atmos,
              startAudioTrack,
            }),
          )
        }, 0)
      })
      if (DEBUG_OVERLAY)
        hls.on(Hls.Events.FRAG_CHANGED, function (event, data) {
          setFrag(` / frag: ${data.frag["_url"]}`)
        })
      hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, () => {
        setAudioTrack(hls.audioTracks[hls.audioTrack].name)
        setSubtitles(hls.subtitleTracks.map((st) => st.name))
        if (currentSubTrack.val === false) hls.subtitleDisplay = false
        else {
          const index = hls.subtitleTracks.findIndex(
            (t) => t.name === currentSubTrack.val,
          )
          hls.subtitleTrack = hls.subtitleTracks.findIndex(
            (t) => t.name === currentSubTrack.val,
          )
          hls.subtitleDisplay = index !== -1
        }
      })
      hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, function () {
        setSubtitles(hls.subtitleTracks.map((st) => st.name))
      })
      hls.on(Hls.Events.DESTROYING, function () {
        setSentryTag("playing", undefined)
      })
      hls.on(Hls.Events.MEDIA_DETACHED, function () {
        setSentryTag("playing", undefined)
      })
      hls.on(Hls.Events.ERROR, (e, data) => {
        console.log(data)
        const hlsErrorMessage = data?.error?.message
        const playerErrorMessage = player.current?.error?.message
        const errorMessage = [hlsErrorMessage, playerErrorMessage]
          .filter((message) => !!message)
          .join(" / ")
        captureSentryException(data, { data: { playerError: errorMessage } })
        if (data?.fatal) {
          if (playbackRetries.current < 2) {
            resetPlayback()
            playbackRetries.current += 1
            return
          }
          playbackRetries.current = 0
          setPrompt({
            open: true,
            onConfirm: () => {
              resetPlayback()
              setFocus(PlayerUIFocusKeys.PlayButton)
            },
            onClose: () => {
              setNavigationProps({ open: false })
              if (returnFocus) setFocus(returnFocus)
            },
            confirmButtonText: t(
              "component__transient_error_retry_button_label",
            ),
            dismissButtonText: t("settings__header_back"),
            dismissible: true,
            title: t("notification__error_generic_title"),
            message: errorMessage,
            icon: "ERROR",
          })
        } else {
          setTimeout(() => {
            hls.startLoad()
            video.play().catch((e) => {
              console.log(e)
              captureSentryException(e)
            })
          }, 500)
        }
        mux.emit(video, "error", {
          player_error_message: errorMessage,
          player_error_context: data?.type ?? "",
        })
      })
      setVideoRef(video)
    }
  }

  useEffect(() => {
    if (url) loadVideo()
    return () => {
      try {
        player.current?.pause()
      } catch (e) {
        console.log(e)
        captureSentryException(e)
      }
      hlsInstance.current?.destroy()
    }
  }, [url])

  const [debug, setDebug] = useState("")
  const [frag, setFrag] = useState("")
  const [is4K, setIs4k] = useState(false)

  const updateDebugOverlay = async () => {
    if (!DEBUG_OVERLAY) return
    if (url && hlsInstance.current) {
      const x = hlsInstance.current.levels[hlsInstance.current.currentLevel]
      if (x) setDebug(x.codecSet + " " + x.height)
      const is4K = (await getPlayerCapabilities()).is4K
      setIs4k(is4K)
    }
  }

  useEffect(() => {
    const k = setInterval(() => {
      updateDebugOverlay()
    }, 500)
    return () => clearInterval(k)
  }, [url, hlsInstance])

  return (
    <>
      {DEBUG_OVERLAY && (
        <DGText
          tx={TI.H01tv}
          sx={{ color: "white", position: "absolute", top: 0, left: 0 }}
        >
          {debug} - {frag} - is4K: {is4K ? "true" : "false"}
        </DGText>
      )}
      <video
        key={url}
        data-testid={"videoplayer"}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "fill",
          opacity: process.env.REACT_APP_TEST ? 0 : 1,
        }}
        ref={player}
      />
    </>
  )
}
